import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ChevronRight } from "lucide-react";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import ProductCard from "@/components/ProductCard";

const CategoryPage = () => {
  const { categoryId } = useParams();
  const [line, setLine] = useState(null);
  const [products, setProducts] = useState([]);
  const [activeSub, setActiveSub] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setActiveSub("");
    api.get("/taxonomy").then(({ data }) => {
      setLine(data.lines.find((l) => l.id === categoryId) || null);
    });
  }, [categoryId]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/products", { params: { category: categoryId, subcategory: activeSub || undefined } })
      .then(({ data }) => setProducts(data))
      .finally(() => setLoading(false));
  }, [categoryId, activeSub]);

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Header />
      <section className="relative overflow-hidden aura border-b border-border">
        <div className="absolute inset-0 bio-grid opacity-30" />
        <div className="relative z-10 px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-14">
          <nav className="flex items-center gap-1 text-xs text-muted-foreground mb-4">
            <Link to="/" className="hover:text-emerald-400">Inicio</Link>
            <ChevronRight className="h-3 w-3" />
            <span className="text-emerald-400">{line?.short || "Categoría"}</span>
          </nav>
          <motion.h1 initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="font-display text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight max-w-3xl">
            {line?.title || "Categoría"}
          </motion.h1>
          <p className="mt-3 text-muted-foreground max-w-2xl">{line?.tagline}</p>

          {line && (
            <div className="mt-6 flex flex-wrap gap-2">
              <button
                onClick={() => setActiveSub("")}
                data-testid="subcat-all"
                className={`rounded-full border px-4 py-2 text-sm transition-all ${!activeSub ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-border hover:border-emerald-500/50 text-muted-foreground"}`}
              >
                Todo
              </button>
              {line.subcategories.map((s) => (
                <button
                  key={s.id}
                  onClick={() => setActiveSub(s.id)}
                  data-testid={`subcat-${s.id}`}
                  className={`rounded-full border px-4 py-2 text-sm transition-all ${activeSub === s.id ? "border-emerald-500 bg-emerald-500/15 text-emerald-300" : "border-border hover:border-emerald-500/50 text-muted-foreground"}`}
                >
                  {s.name}
                </button>
              ))}
            </div>
          )}
        </div>
      </section>

      <section className="px-4 sm:px-8 lg:px-12 max-w-7xl mx-auto py-12">
        {loading ? (
          <p className="text-center text-muted-foreground py-20 font-mono-tech text-sm animate-pulse">Cargando productos…</p>
        ) : products.length === 0 ? (
          <p data-testid="no-products" className="text-center text-muted-foreground py-20">No hay productos en esta sección todavía.</p>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {products.map((p, i) => <ProductCard key={p.id} product={p} index={i} />)}
          </div>
        )}
      </section>
      <Footer />
    </div>
  );
};

export default CategoryPage;
